// Throttled, self-healing thumbnail images for the press-kit grids.
//
// A press kit with "expand all" open, or the review mode re-tiering every
// folder at once, puts a few hundred <img> tags on the page in one go. Each
// thumbnail is a public object in the comms-assets bucket, and almost every
// request is a CDN miss that costs the Storage API two database lookups. The
// project's connection pool is small, so a burst like that gets a large share
// of its requests refused with HTTP 429 -- and a plain <img> that has failed
// once stays a broken icon until something re-renders it.
//
// ThrottledImg fixes both halves:
//   - an image only asks for its bytes once it is near the viewport, and at
//     most THUMB_CONCURRENCY are in flight at a time across the whole page;
//   - a failed load is retried after a growing pause with a cache-busting
//     parameter, so a 429 the pooler served a second ago is not replayed from
//     the browser's cache of the failure; only after the last retry does the
//     tile give up and show the caller's fallback (typically the file name).
//
// When a src changes on a tile that already shows an image (the review mode
// swapping the grid tier for the large one), the old image stays up until
// the new one is granted a slot and loads, so the swap never flashes grey.
import React, { useEffect, useRef, useState } from 'react';
import { createLimiter, unlimited } from './concurrency';

export const THUMB_CONCURRENCY = 6;
// Waits before the 1st, 2nd and 3rd retry. Long enough for a saturated pooler
// to clear, short enough that a tile does not sit blank for long.
export const RETRY_DELAYS_MS = [1500, 4000, 10000];
// How far outside the viewport a tile starts loading. Roughly one screen, so
// a steady scroll keeps finding tiles already filled in.
const NEAR_VIEWPORT = '600px';

const slots = createLimiter(THUMB_CONCURRENCY);

// Tests and very old browsers have no IntersectionObserver; there the image
// loads straight away and untrottled, exactly like a plain <img>.
function canObserve() {
  return typeof IntersectionObserver !== 'undefined';
}

// Appends a retry marker so the browser fetches again instead of replaying a
// failure it remembers for the identical URL. Storage ignores unknown query
// parameters on public object reads.
export function retryUrl(src, attempt) {
  if (!attempt) return src;
  return `${src}${src.includes('?') ? '&' : '?'}retry=${attempt}`;
}

/**
 * Drop-in for <img> with throttling and retries. `fallback` is rendered in
 * place of the image once every retry has failed; `placeholderStyle` shapes
 * the box that holds the tile's space until the image is granted a slot.
 */
export function ThrottledImg({ src, alt, style, placeholderStyle, fallback, ...rest }) {
  const ref = useRef(null);
  // `shown` is the URL currently handed to the <img>. `good` is the last one
  // that fired onload: while a replacement (a retry, or the review mode's
  // larger tier) is pending or has failed, the tile falls back to it rather
  // than to grey or a broken icon. `failed` is only set with nothing to keep.
  const [state, setState] = useState({ shown: null, good: null, failed: false });
  // Written by the effect for the current src, read by the <img> handlers.
  const ctl = useRef(null);

  useEffect(() => {
    if (!src) {
      setState({ shown: null, good: null, failed: false });
      return undefined;
    }
    const run = { cancelled: false, release: null, timer: null, observer: null, attempt: 0 };
    ctl.current = run;
    const gate = canObserve() ? slots : unlimited;

    const finish = () => {
      if (run.release) { const r = run.release; run.release = null; r(); }
    };

    const request = () => {
      gate(() => {
        if (run.cancelled) return undefined;
        setState(s => ({ ...s, shown: retryUrl(src, run.attempt), failed: false }));
        return new Promise(resolve => { run.release = resolve; });
      });
    };

    run.onLoad = () => {
      finish();
      setState(s => ({ ...s, good: s.shown }));
    };
    run.onError = () => {
      finish();
      if (run.cancelled) return;
      const exhausted = run.attempt >= RETRY_DELAYS_MS.length;
      // A failed <img> paints a broken icon, so put the last good image back
      // while waiting, or the blank box if there never was one; a tile that
      // never had one gives up only after the last retry.
      setState(s => {
        if (s.good && s.good !== s.shown) return { ...s, shown: s.good };
        return exhausted ? { shown: null, good: null, failed: true } : { ...s, shown: null };
      });
      if (exhausted) return;
      const delay = RETRY_DELAYS_MS[run.attempt];
      run.attempt += 1;
      run.timer = setTimeout(() => { run.timer = null; request(); }, delay);
    };

    if (!canObserve()) {
      request();
    } else {
      const el = ref.current;
      if (!el) {
        request();
      } else {
        run.observer = new IntersectionObserver(entries => {
          if (!entries.some(e => e.isIntersecting)) return;
          run.observer.disconnect();
          run.observer = null;
          request();
        }, { rootMargin: NEAR_VIEWPORT });
        run.observer.observe(el);
      }
    }

    return () => {
      run.cancelled = true;
      if (run.observer) run.observer.disconnect();
      if (run.timer) clearTimeout(run.timer);
      finish();
    };
  }, [src]);

  if (!src || state.failed) {
    return fallback ?? <div ref={ref} style={{ ...style, ...placeholderStyle }} />;
  }
  if (!state.shown) {
    return <div ref={ref} style={{ ...style, ...placeholderStyle }} aria-hidden="true" />;
  }
  return (
    <img
      ref={ref}
      src={state.shown}
      alt={alt}
      decoding="async"
      style={style}
      onLoad={() => ctl.current?.onLoad?.()}
      onError={() => ctl.current?.onError?.()}
      {...rest}
    />
  );
}
